import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import { IconButton } from '~/components/IconButton';
import { cn } from '~/lib/cn';
import { FEATURE_FLAGS } from '~/modules/experiments/features';
import { useFeatureFlags } from '~/modules/experiments/hooks/useFeatureFlags';

const MUSIC_IQ_TOOLTIP_STORAGE_KEY = 'hasViewedMusicIqTooltip';
const MUSIC_IQ_ROUTE = '/data/music-iq';

export function MusicIqButton({ hideTooltip }: { hideTooltip: boolean }) {
  const { pathname } = useLocation();
  const { t } = useTranslation('wmgone', {
    keyPrefix: 'globalHeader',
  });
  const [isMusicIqChatbotEnabled] = useFeatureFlags([
    FEATURE_FLAGS.WmgDataMusicIqChatbotEnabled,
  ]);
  const [showTooltip, setShowTooltip] = useState(false);
  const isMusicIqRoute = useMemo(
    () => pathname.startsWith(MUSIC_IQ_ROUTE),
    [pathname],
  );

  useEffect(() => {
    const hasViewedMusicIqTooltip = localStorage.getItem(
      MUSIC_IQ_TOOLTIP_STORAGE_KEY,
    );

    setShowTooltip(hasViewedMusicIqTooltip !== 'true');
  }, []);

  const onCloseTooltip = () => {
    localStorage.setItem(MUSIC_IQ_TOOLTIP_STORAGE_KEY, 'true');
    setShowTooltip(false);
  };

  if (!isMusicIqChatbotEnabled) {
    return null;
  }

  return (
    <div className="relative">
      {showTooltip && !isMusicIqRoute && !hideTooltip ? (
        <div className="absolute right-full top-0 z-50 mr-sm flex w-[264px] flex-col gap-md rounded-sm border-px border-solid p-md bg-level-01 border-emphasis-subtle">
          <div className="flex items-center justify-between gap-md">
            <span className="text-body-md font-semibold text-white">
              {t('musicIq.tooltip.title')}
            </span>
            <IconButton
              iconType="xmark"
              onClick={onCloseTooltip}
              aria-label="Close"
              size="xsmall"
              className="text-white"
            />
          </div>
          <span className="text-body-xs leading-4 text-white">
            {t('musicIq.tooltip.description')}
          </span>
        </div>
      ) : null}
      <div
        className={cn(
          'group relative z-0 overflow-hidden rounded-full p-px',
          'before:pointer-events-none before:absolute before:inset-0 before:z-0 before:rounded-full',
          'before:bg-[linear-gradient(90deg,oklch(var(--bg-tertiary-default)),oklch(var(--bg-primary-default)))]',
        )}
      >
        <Link
          to={MUSIC_IQ_ROUTE}
          onClick={onCloseTooltip}
          className={cn(
            'relative z-10 flex h-[40px] w-max items-center gap-sm rounded-full px-[14px]',
            'bg-[linear-gradient(180deg,oklch(var(--bg-level-04))14.29%,oklch(var(--bg-level-02))100%)]',
            'hover:bg-none hover:bg-level-05',
          )}
        >
          <span className="whitespace-nowrap text-body-sm font-bold text-white">
            {t('musicIq.button')}
          </span>
          <img
            src="/images/music-iq/music-iq.svg"
            aria-label="Music IQ Logo"
            alt="Music IQ Logo"
            className="size-[20px]"
          />
        </Link>
      </div>
    </div>
  );
}

MusicIqButton.displayName = 'MusicIqButton';
